import React, { useCallback, useRef } from 'react';
import type { Dispatch } from 'redux';
import { styled } from '@compiled/react';
import flow from 'lodash/flow';
import memoizeOne from 'memoize-one';
import scrollIntoView from 'scroll-into-view-if-needed';
import type { EditorActions } from '@atlaskit/editor-core';
import { useDevOpsAppRecommendationsStore } from '@atlassian/jira-dev-ops-app-recommendations';
import { ff } from '@atlassian/jira-feature-flagging';
import { performPutRequest } from '@atlassian/jira-fetch';
import { getUpdateAnalyticsFlowHelper } from '@atlassian/jira-issue-analytics';
import RichTextInlineEditView, {
	type Props,
} from '@atlassian/jira-issue-internal-fields/src/rich-text/rich-text-inline-edit-view';
import { RICH_CONTENT_FIELD_CONFIRM_ERROR } from '@atlassian/jira-issue-view-common-constants';
import { getIssueModalEditorDropdownPortal } from '@atlassian/jira-issue-view-common-utils';
import connectField from '@atlassian/jira-issue-view-common-views/src/connect-field/connect-field';
import { isDevOpsAppRecommenderEnabled } from '@atlassian/jira-issue-view-common/src/feature-flags';
import { DESCRIPTION } from '@atlassian/jira-issue-view-configurations';
import { withEditExperienceTracker } from '@atlassian/jira-issue-view-experience-tracking';
import { mentionProviderSelector } from '@atlassian/jira-issue-view-services/src/mentions';
import {
	editorExpandedFailure,
	editorSaveFailure,
	editorChangeFailure,
} from '@atlassian/jira-issue-view-store/src/actions/editor-actions';
import { fetchUploadContextRequest } from '@atlassian/jira-issue-view-store/src/common/media/upload-context/upload-context-actions';
import { fetchViewContextRequest } from '@atlassian/jira-issue-view-store/src/common/media/view-context/view-context-actions';
import { activityProviderSelector } from '@atlassian/jira-issue-view-store/src/common/state/selectors/activity-provider-selector';
import { baseUrlSelector } from '@atlassian/jira-issue-view-store/src/common/state/selectors/context-selector';
import {
	fieldEditingAdfValueSelector,
	fieldHtmlValueSelector,
	fieldInvalidSelector,
	fieldInvalidMessageSelector,
} from '@atlassian/jira-issue-view-store/src/common/state/selectors/field-selector';
import { mediaContextSelector } from '@atlassian/jira-issue-view-store/src/common/state/selectors/media-context-selector';
import { richDescriptionContextIdentifierSelector } from '@atlassian/jira-issue-view-store/src/selectors/description-selector';
import { isFieldInTabSelector } from '@atlassian/jira-issue-view-store/src/selectors/tab-selector';
import { messages } from './messages';
import { useRegisterInCommandPalette } from './register-command-palette';

const performPut = <ValueType,>(issueKey: string, value: ValueType) => {
	const url = `/rest/api/3/issue/${issueKey}`;
	return performPutRequest(url, {
		body: JSON.stringify({
			fields: {
				description: value,
			},
		}),
	});
};

const SCROLL_MARGIN_TOP = 100;

export const DescriptionField = (props: Props) => {
	const { onEditRequest, onConfirm } = props;
	const [{ recommendationDetails }, linkPasteActions] = useDevOpsAppRecommendationsStore();

	const editorActionsRef = useRef<EditorActions | null>(null);
	const editorRef = useRef<HTMLDivElement | null>(null);
	const focusOnEditor = useCallback(() => {
		if (editorActionsRef?.current) {
			editorActionsRef.current.focus();
		}
	}, [editorActionsRef]);
	const onEditRequestHandler = useCallback(() => {
		if (ff('issue-update-analytics-events-on-issue-view')) {
			getUpdateAnalyticsFlowHelper().setAttributes('description', {
				isCommandPaletteEditing: false,
			});
		}
		onEditRequest();
	}, [onEditRequest]);

	const onCompalEditRequestHandler = useCallback(() => {
		if (ff('issue-update-analytics-events-on-issue-view')) {
			getUpdateAnalyticsFlowHelper().setAttributes('description', {
				isCommandPaletteEditing: true,
			});
		}
		onEditRequest();
		// prevent race condition when editor has not finished rendering immediately
		setTimeout(() => {
			focusOnEditor?.();
			if (editorRef?.current) {
				if (editorRef.current.offsetHeight > window.innerHeight / 2) {
					editorRef.current.scrollIntoView({
						block: 'end',
					});
				} else {
					scrollIntoView(editorRef.current, {
						block: 'start',
						scrollMode: 'if-needed',
						// this behaves like scrollMarginTop style property
						behavior: (actions) =>
							actions.forEach(({ el, top }) => {
								// eslint-disable-next-line no-param-reassign
								el.scrollTop = top - SCROLL_MARGIN_TOP;
							}),
					});
				}
			}
		});
	}, [onEditRequest, focusOnEditor, editorRef]);

	const onConfirmHandler = useCallback(() => {
		onConfirm();
		if (isDevOpsAppRecommenderEnabled() && recommendationDetails) {
			linkPasteActions.setShowRecommendation(true);
		}
	}, [onConfirm, recommendationDetails, linkPasteActions]);

	useRegisterInCommandPalette({ onEditRequest: onCompalEditRequestHandler });

	return (
		<EditorContainer ref={editorRef}>
			<RichTextInlineEditView
				{...props}
				onEditRequest={onEditRequestHandler}
				onConfirm={onConfirmHandler}
				editorActionsRef={editorActionsRef}
			/>
		</EditorContainer>
	);
};

const onExpandedFailureFactory = memoizeOne(
	(dispatch: Dispatch) => () => dispatch(editorExpandedFailure()),
);
const onSaveFailureFactory = memoizeOne(
	(dispatch: Dispatch) => () => dispatch(editorSaveFailure()),
);

const onChangeFailureFactory = memoizeOne(
	(dispatch: Dispatch) => () => dispatch(editorChangeFailure()),
);

const onViewRefreshFactory = memoizeOne(
	(dispatch: Dispatch) => () => dispatch(fetchViewContextRequest()),
);

const onUploadRefreshFactory = memoizeOne(
	(dispatch: Dispatch) => () => dispatch(fetchUploadContextRequest()),
);

export default flow(
	withEditExperienceTracker(DESCRIPTION),
	connectField(() => ({
		fieldId: DESCRIPTION,
		shouldSaveDraft: true,
		isOptimistic: true,
		saveField: ({ issueKey, value }) => performPut(issueKey, value),
		canContainMediaContent: true,
		onSaveFailureFlagType: RICH_CONTENT_FIELD_CONFIRM_ERROR,
		// @ts-expect-error - TS2322 - Type '(state: Readonly<{ agile: Agile; context: ContextState; entities: Readonly<{ applicationRoles?: ApplicationRole[] | undefined; cardCover: CardCover; childrenIssues: ChildrenIssuesState; ... 29 more ...; myPreferences?: Partial<...> | undefined; }>; ... 5 more ...; validators: Validators; }>, intl: IntlShapeV2) => { ...' is not assignable to type 'AdditionalProps<unknown>'.
		additionalProps: (state, intl) => {
			const baseUrl = baseUrlSelector(state);
			return {
				baseUrl,
				portalElement: isFieldInTabSelector(DESCRIPTION)(state)
					? // Using a portal element here because otherwise dropdowns would get cut off
						// See: https://jdog.jira-dev.com/browse/BENTO-4100
						getIssueModalEditorDropdownPortal() || document.body
					: undefined,
				adfValue: fieldEditingAdfValueSelector(DESCRIPTION)(state),
				htmlValue: fieldHtmlValueSelector(DESCRIPTION)(state),
				isInvalid: fieldInvalidSelector(DESCRIPTION)(state),
				invalidMessage: fieldInvalidMessageSelector(DESCRIPTION)(state),
				mediaContext: mediaContextSelector(state),
				mentionProvider: mentionProviderSelector(state),
				contextIdentifier: richDescriptionContextIdentifierSelector(state),
				activityProvider: activityProviderSelector(state),
				externalId: 'issue.descriptionHtml',
				noValueText: intl.formatMessage(messages.noValueText),
			};
		},
		additionalCallbacks: (dispatch) => ({
			onExpandedFailure: onExpandedFailureFactory(dispatch),
			onSaveFailure: onSaveFailureFactory(dispatch),
			onChangeFailure: onChangeFailureFactory(dispatch),
			onViewRefresh: onViewRefreshFactory(dispatch),
			onUploadRefresh: onUploadRefreshFactory(dispatch),
		}),
	})),
)(DescriptionField);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EditorContainer = styled.div({
	scrollMarginBottom: '100px',
});
